// components/customer/CustomerNoteModal.jsx
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useFormContext } from "react-hook-form";

// Import inhouse icons
import { X } from "lucide-react";

export default function CustomerNoteModal({ initialNote = "", onClose }) {
  const [portalNode, setPortalNode] = useState(null);
  const [noteInput, setNoteInput] = useState(initialNote || "");
  const maxLength = 5000;

  const { setValue } = useFormContext();

  useEffect(() => {
    const node = document.createElement("div");
    node.setAttribute("data-customer-note-modal-root", "true");
    document.body.appendChild(node);
    setPortalNode(node);

    const handleEsc = (event) => {
      if (event.key === "Escape") onClose?.();
    };

    document.addEventListener("keydown", handleEsc);
    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.removeChild(node);
      setPortalNode(null);
    };
  }, [onClose]);

  if (!portalNode) return null;

  const handleSave = () => {
    setValue("notes", noteInput.trim());
    onClose?.();
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[50] flex items-center justify-center bg-[#000]/45 px-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex w-[600px] max-h-[95%] flex-col overflow-hidden rounded-lg bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between rounded-t-lg bg-[#ececec] px-6 py-4">
          <span className="font-medium text-[#000]">Add note</span>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 transition hover:bg-[#f3f4f6]"
            aria-label="Close modal"
          >
            <X className="h-5 w-5 text-[#333333]" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <textarea
            value={noteInput}
            onChange={(e) => {
              if (e.target.value.length <= maxLength) {
                setNoteInput(e.target.value);
              }
            }}
            rows={6}
            placeholder="Add a note"
            className="w-full resize-none rounded-md border border-[#93939355] p-3 text-sm outline-none focus:border-[#333333a3]"
          />

          <div className="mt-2 text-right text-xs text-[#333333]">
            {noteInput.length}/{maxLength}
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 py-3 px-5 border-t border-[#d2d2d2]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#333333] bg-white border border-[#9e9e9e] rounded-[10px] hover:bg-[#f3f3f3]"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 text-sm font-medium text-white rounded-[10px] transition bg-[#333333] hover:bg-[#333333db]"
            disabled={noteInput.trim() === ""}
          >
            Save
          </button>
        </div>
      </div>
    </div>,
    portalNode
  );
};